import { NavLink, Link, Navigate, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { LogOut, LayoutDashboard, DollarSign } from "lucide-react";
import { useAuth, authHeaders } from "../../lib/auth";
import { api } from "../../lib/api";
import PageShell from "../../components/marketing/PageShell";
import BrepLogo from "../../components/BrepLogo";

export default function CoachLayout() {
  const { user, token, ready, logout } = useAuth();
  const [coach, setCoach] = useState(undefined);
  const loc = useLocation();

  useEffect(() => {
    if (!token) { setCoach(null); return; }
    api.get("/coach/me", { headers: authHeaders(token) })
      .then((r) => setCoach(r.data))
      .catch(() => setCoach(null));
  }, [token]);

  if (!ready) return <PageShell><div className="p-12 text-white/40">Loading…</div></PageShell>;
  if (!user) return <Navigate to="/" replace state={{ from: loc.pathname }} />;
  if (coach === undefined) return <PageShell><div className="p-12 text-white/40">Checking access…</div></PageShell>;
  if (!coach) return (
    <PageShell testid="page-coach-forbidden">
      <div className="p-12 max-w-lg">
        <h1 className="font-anton uppercase text-4xl">Coach access only</h1>
        <p className="text-white/60 mt-4">Your account isn't approved as a coach yet. If you've applied, we'll be in touch.</p>
        <Link to="/dashboard" className="btn-ghost mt-8 inline-block px-5 py-3 text-[11px] tracking-[0.24em] uppercase">Back to my dashboard</Link>
      </div>
    </PageShell>
  );

  const cls = ({ isActive }) => `flex items-center gap-3 px-4 py-3 text-[11px] tracking-[0.22em] uppercase transition-colors border-l-2 ${isActive ? "border-[#2A5BFF] text-white bg-white/[0.02]" : "border-transparent text-white/60 hover:text-white"}`;

  return (
    <PageShell testid="page-coach">
      <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[240px_1fr]">
        <aside className="border-b lg:border-b-0 lg:border-r border-white/10 bg-[#050510] sticky top-0 z-40" data-testid="coach-sidebar">
          <div className="p-6 border-b border-white/10 flex items-center justify-between">
            <Link to="/"><BrepLogo size="sm" /></Link>
            <span className="text-[9px] tracking-[0.3em] uppercase text-[#2A5BFF]">Coach</span>
          </div>
          <nav className="flex lg:flex-col overflow-x-auto lg:overflow-visible py-2">
            <NavLink to="/coach" end className={cls} data-testid="coach-nav-dashboard">
              <LayoutDashboard size={14} /> <span className="hidden sm:inline">Dashboard</span>
            </NavLink>
            <NavLink to="/coach/earnings" className={cls} data-testid="coach-nav-earnings">
              <DollarSign size={14} /> <span className="hidden sm:inline">Earnings</span>
            </NavLink>
            <button onClick={logout} className="flex items-center gap-3 px-4 py-3 text-[11px] tracking-[0.22em] uppercase text-white/60 hover:text-white border-l-2 border-transparent" data-testid="coach-nav-logout">
              <LogOut size={14} /> <span className="hidden sm:inline">Logout</span>
            </button>
          </nav>
          <div className="hidden lg:block px-4 pb-6">
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/40">Coach</div>
            <div className="text-white/80 text-sm mt-1 truncate">{coach.first_name} · {coach.email}</div>
          </div>
        </aside>
        <main className="min-h-screen"><Outlet /></main>
      </div>
    </PageShell>
  );
}
